import { useState, useEffect } from 'react';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, CircleDot, Loader2, Plus, Eye, Pencil, Download, FileText, Calendar, CalendarDays, Ruler, MapPin, Banknote, MoreHorizontal, User, Send, Mail, Upload, CheckCircle2, Image, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { usePermissions } from '../contexts/PermissionsContext';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { fr } from 'date-fns/locale';
import { DevisFormDialog } from '../components/devis/DevisFormDialog';
import { SendDevisEmailDialog } from '../components/devis/SendDevisEmailDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const DEVIS_STATUTS = {
    a_envoyer: { label: 'À envoyer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    relance_1: { label: 'Relancé 1', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_2: { label: 'Relancé 2', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_3: { label: 'Relancé 3', color: 'bg-red-100 text-red-700 border-red-200' },
    interesse: { label: 'Intéressé', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700 border-red-200' },
    sans_reponse: { label: 'Sans réponse', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const STATUT_BORDER_COLORS = {
    a_envoyer: 'border-amber-400',
    envoye: 'border-blue-400',
    relance_1: 'border-orange-400',
    relance_2: 'border-orange-400',
    relance_3: 'border-red-400',
    interesse: 'border-cyan-400',
    accepte: 'border-emerald-400',
    refuse: 'border-red-400',
    sans_reponse: 'border-slate-400',
};

const FORMULE_LABELS = {
    economique: 'Économique',
    economique_plus: 'Économique Plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

const FORMULE_COLORS = {
    economique: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: 'text-[#2e7cd6]' },
    economique_plus: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: 'text-[#2e7cd6]' },
    standard: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: 'text-[#2e7cd6]' },
    luxe: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: 'text-[#2e7cd6]' },
};

const FORMULE_ORDER = ['economique', 'economique_plus', 'standard', 'luxe'];

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try {
        return format(new Date(dateStr), 'dd MMMM yyyy', { locale: fr });
    } catch {
        return dateStr;
    }
}

export default function DevisDossierPage() {
    const { inventaireId } = useParams();
    const navigate = useNavigate();
    const { hasFlag, can } = usePermissions();
    const LOCKED_STATUTS = new Set(['envoye', 'relance_1', 'relance_2', 'relance_3', 'interesse', 'accepte', 'refuse', 'sans_reponse']);
    const devisLocked = (d) => {
        if (can('devis', 'write') === false) return true;
        if (hasFlag('lock_devis_after_envoye') && LOCKED_STATUTS.has(d.statut)) return true;
        return false;
    };
    const [devisList, setDevisList] = useState([]);
    const [inventaire, setInventaire] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creatingFormule, setCreatingFormule] = useState(null);
    const [previewDevis, setPreviewDevis] = useState(null);
    const [editingDevis, setEditingDevis] = useState(null);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [config, setConfig] = useState(null);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [emailHistory, setEmailHistory] = useState([]);
    const [relanceDialog, setRelanceDialog] = useState(null); // 'relance_1' | 'relance_2' | 'relance_3' | null
    const [relanceFile, setRelanceFile] = useState(null);
    const [relanceUploading, setRelanceUploading] = useState(false);
    const [relanceJustificatifs, setRelanceJustificatifs] = useState([]);
    const [dateRelance2, setDateRelance2] = useState('');
    const [remarqueRelance2, setRemarqueRelance2] = useState('');
    const [dateRelance3, setDateRelance3] = useState('');
    const [remarqueRelance3, setRemarqueRelance3] = useState('');
    const [interesseDialog, setInteresseDialog] = useState(false);
    const [remarqueInteresse, setRemarqueInteresse] = useState('');

    // Surveille les verrous actifs sur tous les devis du dossier + l'inventaire
    // pour désactiver le Select de statut si quelqu'un d'autre est en train de modifier
    const devisDossierLocks = useBulkLocks('devis', devisList.map(d => d.id));
    const inventaireLocksMap = useBulkLocks('inventaire', inventaireId ? [inventaireId] : []);
    const dossierLockedBy = Object.values(devisDossierLocks)[0] || (inventaireId ? inventaireLocksMap[inventaireId] : null);
    // Quand le flag "Verrouiller un devis après envoi" est actif ET qu'au moins un devis du dossier
    // a déjà été envoyé (ou statut dérivé), on bloque l'envoi et la création de nouveaux devis.
    const dossierLockedByFlag = hasFlag('lock_devis_after_envoye')
        && devisList.some(d => LOCKED_STATUTS.has(d.statut));
    const [interesseSaving, setInteresseSaving] = useState(false);
    const [accepteDialog, setAccepteDialog] = useState(false);
    const [accepteDevisId, setAccepteDevisId] = useState(null);
    const [remarqueAccepte, setRemarqueAccepte] = useState('');
    const [refuseDialog, setRefuseDialog] = useState(false);
    const [remarqueRefuse, setRemarqueRefuse] = useState('');
    const [refuseSaving, setRefuseSaving] = useState(false);

    const fetchData = async () => {
        try {
            const [devisRes, invRes, configRes, histRes, justifRes] = await Promise.all([
                axios.get(`${API}/api/devis/dossier/${inventaireId}`, { withCredentials: true }),
                axios.get(`${API}/api/inventaires/${inventaireId}`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/inventaires/config`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/devis/dossier/${inventaireId}/email-history`, { withCredentials: true }).catch(() => null),
                axios.get(`${API}/api/devis/dossier/${inventaireId}/relance-justificatifs`, { withCredentials: true }).catch(() => null),
            ]);
            setDevisList(devisRes.data);
            if (invRes) setInventaire(invRes.data);
            if (configRes) setConfig(configRes.data);
            if (histRes) setEmailHistory(histRes.data);
            if (justifRes) setRelanceJustificatifs(justifRes.data);
        } catch {
            toast.error('Erreur lors du chargement du dossier');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [inventaireId]);

    const clientNom = devisList[0]?.client_nom || inventaire?.client_nom || inventaire?.nom || 'Client';
    const clientTel = devisList[0]?.client_telephone || inventaire?.telephone || '';
    const clientEmail = devisList[0]?.client_email || inventaire?.email || '';
    const adresseDepart = inventaire?.adresse_depart;
    const adresseArrivee = inventaire?.adresse_arrivee;

    // Statut global
    const activeDevis = devisList.filter(d => d.est_actif);
    const statuts = activeDevis.map(d => d.statut);
    const hasAcceptedDevis = statuts.includes('accepte');
    // On ne considère que les statuts "progressables" (en attente d'action) pour l'affichage
    // — un devis refusé/sans-réponse est final et ne doit pas masquer un devis accepté.
    const PROGRESSABLE_STATUTS = ['a_envoyer', 'envoye', 'relance_1', 'relance_2', 'relance_3', 'interesse'];
    const otherActiveStatuts = activeDevis
        .filter(d => d.statut !== 'accepte' && PROGRESSABLE_STATUTS.includes(d.statut))
        .map(d => d.statut);
    // Si un devis accepté cohabite avec d'autres devis actifs PROGRESSABLES,
    // afficher le statut des autres (pour que l'utilisateur puisse les faire progresser).
    // Sinon → "accepté" prime sur tout statut final (refusé / sans réponse) d'un autre devis.
    const mainStatut = (hasAcceptedDevis && otherActiveStatuts.length > 0)
        ? (otherActiveStatuts.includes('interesse') ? 'interesse'
            : otherActiveStatuts.includes('envoye') ? 'envoye'
            : otherActiveStatuts.includes('relance_3') ? 'relance_3'
            : otherActiveStatuts.includes('relance_2') ? 'relance_2'
            : otherActiveStatuts.includes('relance_1') ? 'relance_1'
            : otherActiveStatuts[0] || 'a_envoyer')
        : hasAcceptedDevis ? 'accepte'
        : statuts.includes('interesse') ? 'interesse'
        : statuts.includes('envoye') ? 'envoye'
        : statuts.includes('relance_3') ? 'relance_3'
        : statuts.includes('relance_2') ? 'relance_2'
        : statuts.includes('relance_1') ? 'relance_1'
        : statuts.includes('refuse') ? 'refuse'
        : statuts.includes('sans_reponse') ? 'sans_reponse'
        : statuts[0] || 'a_envoyer';
    const statutInfo = DEVIS_STATUTS[mainStatut] || DEVIS_STATUTS.a_envoyer;
    const borderColor = STATUT_BORDER_COLORS[mainStatut] || 'border-blue-200';

    const handleStatusChange = async (newStatut) => {
        if (newStatut.startsWith('relance_')) {
            setRelanceDialog(newStatut);
            setRelanceFile(null);
            return;
        }
        if (newStatut === 'interesse') {
            setInteresseDialog(true);
            setRemarqueInteresse('');
            return;
        }
        if (newStatut === 'accepte') {
            setAccepteDialog(true);
            setAccepteDevisId(null);
            setRemarqueAccepte('');
            return;
        }
        if (newStatut === 'refuse') {
            setRefuseDialog(true);
            setRemarqueRefuse('');
            return;
        }
        try {
            await axios.patch(
                `${API}/api/devis/dossier/${inventaireId}/statut?statut=${newStatut}`,
                {},
                { withCredentials: true }
            );
            toast.success('Statut mis à jour');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const handleInteresseConfirm = async () => {
        if (!remarqueInteresse.trim()) { toast.error('Veuillez ajouter une remarque'); return; }
        setInteresseSaving(true);
        try {
            await axios.post(
                `${API}/api/devis/dossier/${inventaireId}/interesse`,
                { remarque_interesse: remarqueInteresse },
                { withCredentials: true }
            );
            toast.success('Statut mis à jour : Intéressé');
            setInteresseDialog(false);
            setRemarqueInteresse('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setInteresseSaving(false);
        }
    };


    const handleAccepteConfirm = async () => {
        if (!accepteDevisId) { toast.error('Veuillez choisir le devis accepté'); return; }
        if (!remarqueAccepte.trim()) { toast.error('Veuillez ajouter une remarque'); return; }
        try {
            await axios.post(
                `${API}/api/devis/dossier/${inventaireId}/accepter`,
                { devis_id: accepteDevisId, remarque_accepte: remarqueAccepte },
                { withCredentials: true }
            );
            toast.success('Devis accepté');
            setAccepteDialog(false);
            setAccepteDevisId(null);
            setRemarqueAccepte('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleRefuseConfirm = async () => {
        if (!remarqueRefuse.trim()) { toast.error('Veuillez ajouter une remarque'); return; }
        setRefuseSaving(true);
        try {
            await axios.patch(
                `${API}/api/devis/dossier/${inventaireId}/statut?statut=refuse`,
                {},
                { withCredentials: true }
            );
            // Store remarque on all active devis
            const activeDevis = devisList.filter(d => d.est_actif);
            for (const d of activeDevis) {
                await axios.patch(
                    `${API}/api/devis/${d.id}/remarque-refuse`,
                    { remarque_refuse: remarqueRefuse },
                    { withCredentials: true }
                ).catch(() => {});
            }
            toast.success('Statut mis à jour : Refusé');
            setRefuseDialog(false);
            setRemarqueRefuse('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setRefuseSaving(false);
        }
    };

    const handleRelanceConfirm = async () => {
        if (!relanceFile) { toast.error('Veuillez ajouter un justificatif'); return; }
        if (relanceDialog === 'relance_1' && !dateRelance2) {
            toast.error('Veuillez programmer la date de relance 2');
            return;
        }
        if (relanceDialog === 'relance_2' && !dateRelance3) {
            toast.error('Veuillez programmer la date de relance 3');
            return;
        }
        setRelanceUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', relanceFile);
            formData.append('relance', relanceDialog);
            await axios.post(
                `${API}/api/devis/dossier/${inventaireId}/relance-justificatif`,
                formData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
            await axios.patch(
                `${API}/api/devis/dossier/${inventaireId}/statut?statut=${relanceDialog}`,
                {},
                { withCredentials: true }
            );
            // Programme next relance
            if (relanceDialog === 'relance_1' && dateRelance2) {
                await axios.post(
                    `${API}/api/devis/dossier/${inventaireId}/programme-relance-2`,
                    { date_relance_2: dateRelance2, remarque_relance_2: remarqueRelance2 || '' },
                    { withCredentials: true }
                );
            }
            if (relanceDialog === 'relance_2' && dateRelance3) {
                await axios.post(
                    `${API}/api/devis/dossier/${inventaireId}/programme-relance-3`,
                    { date_relance_3: dateRelance3, remarque_relance_3: remarqueRelance3 || '' },
                    { withCredentials: true }
                );
            }
            toast.success(`Statut mis à jour : ${DEVIS_STATUTS[relanceDialog]?.label}`);
            setRelanceDialog(null);
            setRelanceFile(null);
            setDateRelance2(''); setRemarqueRelance2('');
            setDateRelance3(''); setRemarqueRelance3('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setRelanceUploading(false);
        }
    };

    // Determine which statuts are available based on current status
    const getAvailableStatuts = () => {
        // These statuts can never be selected manually once passed
        const lockedStatuts = ['a_envoyer', 'envoye'];
        const finalStatuts = ['sans_reponse', 'accepte', 'refuse'];
        const relanceProgression = { relance_1: 1, relance_2: 2, relance_3: 3 };
        const currentRelanceLevel = relanceProgression[mainStatut] ?? 0;
        const isFinal = finalStatuts.includes(mainStatut);
        const isInteresse = mainStatut === 'interesse';

        return Object.entries(DEVIS_STATUTS).filter(([value]) => {
            // Never go back to a_envoyer or envoye
            if (lockedStatuts.includes(value)) return false;
            // If final status, nothing changes
            if (isFinal) return false;
            // If interessé: only accepte, refuse, sans_reponse
            if (isInteresse) {
                return ['accepte', 'refuse', 'sans_reponse'].includes(value);
            }
            // Sequential relance: can't skip levels and can't go back
            if (value === 'relance_1' && currentRelanceLevel >= 1) return false;
            if (value === 'relance_2' && (currentRelanceLevel < 1 || currentRelanceLevel >= 2)) return false;
            if (value === 'relance_3' && (currentRelanceLevel < 2 || currentRelanceLevel >= 3)) return false;
            // Sans reponse only after relance 3
            if (value === 'sans_reponse' && currentRelanceLevel < 3) return false;
            return true;
        });
    };

    // Formules from inventaire
    const formules = (inventaire?.formules || []).sort((a, b) => FORMULE_ORDER.indexOf(a) - FORMULE_ORDER.indexOf(b));

    const handleDownloadPdf = async (devisId) => {
        try {
            const res = await axios.get(`${API}/api/devis/${devisId}/pdf?preview=true`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const name = res.headers['content-disposition']?.split('filename="')[1]?.replace('"', '') || `devis_${devisId}.pdf`;
            setPdfPreviewUrl(url);
            setPdfFilename(name);
        } catch {
            toast.error('Erreur lors du chargement du PDF');
        }
    };

    const handleClosePdfPreview = () => {
        if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
        setPdfPreviewUrl(null);
        setPdfFilename('');
    };

    const handleConfirmDownload = () => {
        if (!pdfPreviewUrl) return;
        const a = document.createElement('a');
        a.href = pdfPreviewUrl;
        a.download = pdfFilename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // Group devis by formule
    const devisByFormule = {};
    for (const d of devisList) {
        if (!devisByFormule[d.formule]) devisByFormule[d.formule] = [];
        devisByFormule[d.formule].push(d);
    }

    // Inventaire data (utilisé en fallback uniquement si un devis n'a pas
    // sa propre valeur — chaque devis stocke désormais son volume/distance
    // dans `parametres`).
    const infosBase = inventaire?.calcul_prix?.infos_base || {};
    const fallbackVolume = infosBase.volume_devis;
    const fallbackDistance = infosBase.distance;

    // Renvoie le volume/distance propre à un devis (avec fallback inventaire
    // pour les très anciens devis sans `parametres.volume_devis`).
    const devisVolume = (devis) => {
        const v = devis?.parametres?.volume_devis;
        return (v !== undefined && v !== null && v !== '') ? v : (fallbackVolume || '-');
    };
    const devisDistance = (devis) => {
        const d = devis?.parametres?.distance;
        return (d !== undefined && d !== null && d !== '') ? d : (fallbackDistance || '-');
    };

    const handleCreateDevis = async (formule) => {
        setCreatingFormule(formule);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full" data-testid="devis-dossier-page">
            <ReadOnlyGuard module="devis">
            {/* Sticky Header */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/devis')}
                            data-testid="devis-back-btn"
                        >
                            <ArrowLeft size={16} className="mr-2" />
                            Retour
                        </Button>
                        <h1 className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Aperçu devis — {clientNom}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/inventaires?open=${inventaireId}`)}
                            data-testid="devis-inventaire-btn"
                        >
                            <ClipboardList size={16} className="mr-2" />
                            Aller dans inventaire
                        </Button>
                        <Button
                            onClick={() => setShowEmailDialog(true)}
                            className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                            data-testid="devis-send-email-btn"
                            disabled={dossierLockedByFlag}
                            title={dossierLockedByFlag ? "Verrou actif : un devis a déjà été envoyé" : ''}
                        >
                            <Send size={16} className="mr-2" />
                            Envoyer
                        </Button>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="max-w-5xl mx-auto space-y-6">
                    {/* Section 1: Statut du devis */}
                    <Card className={`bg-blue-50 border-2 ${borderColor}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                1. Statut du devis
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                {mainStatut === 'a_envoyer' ? (
                                    <Badge variant="outline" className={`${statutInfo.color} border text-sm px-3 py-1`}>
                                        {statutInfo.label}
                                    </Badge>
                                ) : (
                                    <>
                                        <Select value={mainStatut} onValueChange={handleStatusChange} disabled={!!dossierLockedBy}>
                                            <SelectTrigger className="w-[250px] bg-white" data-testid="devis-dossier-status-select" title={dossierLockedBy ? `${dossierLockedBy.user_name} est en train de modifier ce dossier` : ''}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {getAvailableStatuts().map(([value, info]) => (
                                                    <SelectItem key={value} value={value}>
                                                        <span className={`inline-block px-2 py-0.5 rounded text-sm ${info.color}`}>
                                                            {info.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Badge variant="outline" className={`${statutInfo.color} border text-sm px-3 py-1`}>
                                            {statutInfo.label}
                                        </Badge>
                                    </>
                                )}
                                {mainStatut === 'a_envoyer' && (
                                    <span className="text-xs text-slate-400 italic">Envoyez le devis pour modifier le statut</span>
                                )}
                                {activeDevis.length > 1 && (
                                    <span className="text-xs text-slate-500">
                                        ({activeDevis.length} devis actifs)
                                    </span>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 2: Contact client */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />
                                2. Contact client
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6">
                                <div>
                                    <span className="text-xs text-slate-400">Nom</span>
                                    <p className="text-sm font-medium text-slate-800">{clientNom}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Téléphone</span>
                                    <p className="text-sm text-slate-700">{clientTel || <span className="italic text-slate-400">-</span>}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Email</span>
                                    <p className="text-sm text-slate-700">{clientEmail || <span className="italic text-slate-400">-</span>}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Commercial</span>
                                    <p className="text-sm text-slate-700">
                                        {devisList[0]?.commercial_name || inventaire?.commercial_name || <span className="italic text-slate-400">Non assigné</span>}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Type</span>
                                    <p className="text-sm text-slate-700">{inventaire?.type_inventaire || <span className="italic text-slate-400">-</span>}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400">Source & Département</span>
                                    <p className="text-sm text-slate-700">
                                        {[inventaire?.source, inventaire?.departement].filter(Boolean).join(' — ') || <span className="italic text-slate-400">-</span>}
                                    </p>
                                </div>
                                <div className="sm:col-span-2 lg:col-span-3 border-t border-slate-100 pt-3 mt-1">
                                    <div className="flex items-stretch gap-0 rounded-lg border border-slate-200 overflow-hidden">
                                        <div className="flex-1 px-4 py-3 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <MapPin size={12} className="text-emerald-500 shrink-0" />
                                                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Départ</span>
                                            </div>
                                            <p className="text-sm text-slate-800">{adresseDepart?.adresse || <span className="italic text-slate-400">Non renseignée</span>}</p>
                                        </div>
                                        <div className="flex items-center justify-center px-4 bg-slate-50 border-x border-slate-200">
                                            <div className="flex items-center gap-1 text-slate-400">
                                                <div className="w-4 h-[1.5px] bg-slate-300 rounded" />
                                                <div className="w-4 h-[1.5px] bg-slate-300 rounded" />
                                                <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] border-l-[#2e7cd6]" />
                                            </div>
                                        </div>
                                        <div className="flex-1 px-4 py-3 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <MapPin size={12} className="text-red-500 shrink-0" />
                                                <span className="text-xs font-medium text-red-600 uppercase tracking-wide">Arrivée</span>
                                            </div>
                                            <p className="text-sm text-slate-800">{adresseArrivee?.adresse || <span className="italic text-slate-400">Non renseignée</span>}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Sections par formule */}
                    {formules.map((formule, idx) => {
                        const formuleDevis = (devisByFormule[formule] || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        const activeFormuleDevis = formuleDevis.filter(d => d.est_actif);
                        const historicFormuleDevis = formuleDevis.filter(d => d.est_modifie);
                        const colors = FORMULE_COLORS[formule] || FORMULE_COLORS.standard;
                        const label = FORMULE_LABELS[formule] || formule;
                        const formulePrix = inventaire?.calcul_prix?.formules_data?.[formule]?.total_ttc;

                        return (
                            <Card key={formule} className={`border-slate-200`} data-testid={`formule-section-${formule}`}>
                                <CardHeader className="pb-2 border-b border-slate-100">
                                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                        <FileText size={16} className={colors.icon} />
                                        {idx + 3}. {label}
                                        <Badge variant="outline" className={`${colors.bg} ${colors.text} ${colors.border} border ml-2`}>
                                            {activeFormuleDevis.length} devis
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    {activeFormuleDevis.length === 0 && historicFormuleDevis.length === 0 ? (
                                        /* Empty state - bouton créer */
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <p className="text-sm text-slate-400 mb-4">Aucun devis pour cette formule</p>
                                            <Button
                                                variant="outline"
                                                onClick={() => handleCreateDevis(formule)}
                                                data-testid={`create-devis-${formule}`}
                                                disabled={dossierLockedByFlag}
                                                title={dossierLockedByFlag ? "Verrou actif : un devis a déjà été envoyé" : ''}
                                            >
                                                <Plus size={16} className="mr-2" />
                                                Créer un devis {label}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {/* Devis actifs */}
                                            {activeFormuleDevis.map(devis => {
                                                const isEnvoye = devis.statut === 'envoye' || devis.statut === 'relance_1' || devis.statut === 'relance_2' || devis.statut === 'relance_3';
                                                const isAccepte = devis.statut === 'accepte';
                                                const acceptedDevis = activeDevis.find(d => d.statut === 'accepte');
                                                const isRefusedByAccept = acceptedDevis && devis.statut === 'refuse' && acceptedDevis.id !== devis.id;
                                                const borderClass = isAccepte ? 'border-emerald-500 bg-emerald-50/30 hover:bg-emerald-50/50'
                                                    : isRefusedByAccept ? 'border-red-400 bg-red-50/20 hover:bg-red-50/30'
                                                    : isEnvoye ? 'border-[#2e7cd6] bg-blue-50/30 hover:bg-blue-50/50'
                                                    : 'border-slate-200 bg-white hover:bg-slate-50';
                                                return (
                                                    <div
                                                        key={devis.id}
                                                        className={`p-4 rounded-lg border transition-colors ${borderClass}`}
                                                        data-testid={`devis-card-${devis.id}`}
                                                    >
                                                        {isAccepte && (
                                                            <div className="flex items-center gap-1.5 mb-2">
                                                                <CheckCircle2 size={12} className="text-emerald-600" />
                                                                <span className="text-xs font-medium text-emerald-600">Devis accepté</span>
                                                            </div>
                                                        )}
                                                        {isRefusedByAccept && (
                                                            <div className="flex items-center gap-1.5 mb-2">
                                                                <span className="text-xs font-medium text-red-500">Devis refusé — accepté : {acceptedDevis.numero_devis}</span>
                                                            </div>
                                                        )}
                                                        {!isAccepte && !isRefusedByAccept && isEnvoye && (
                                                            <div className="flex items-center gap-1.5 mb-2">
                                                                <Mail size={12} className="text-[#2e7cd6]" />
                                                                <span className="text-xs font-medium text-[#2e7cd6]">Devis envoyé</span>
                                                            </div>
                                                        )}
                                                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center">
                                                            {/* Numéro */}
                                                            <div>
                                                                <span className="text-xs text-slate-400">N° Devis</span>
                                                                <p className="font-mono text-sm font-medium text-slate-800">{devis.numero_devis}</p>
                                                            </div>
                                                            {/* Date création */}
                                                            <div>
                                                                <span className="text-xs text-slate-400">Créé le</span>
                                                                <p className="text-sm text-slate-700">{formatDate(devis.created_at)}</p>
                                                            </div>
                                                            {/* Volume */}
                                                            <div>
                                                                <span className="text-xs text-slate-400">Volume</span>
                                                                <p className="text-sm text-slate-700">{devisVolume(devis)} m³</p>
                                                            </div>
                                                            {/* Distance */}
                                                            <div>
                                                                <span className="text-xs text-slate-400">Distance</span>
                                                                <p className="text-sm text-slate-700">{devisDistance(devis)} km</p>
                                                            </div>
                                                            {/* Prix */}
                                                            <div>
                                                                <span className="text-xs text-slate-400">Prix</span>
                                                                <p className="text-sm font-semibold text-slate-800">
                                                                    {devis.prix_final != null ? `${devis.prix_final.toFixed(2)} €` : (formulePrix ? `${formulePrix} € (calc)` : '-')}
                                                                </p>
                                                            </div>
                                                            {/* Actions dropdown */}
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="icon" data-testid={`devis-actions-${devis.id}`}>
                                                                        <MoreHorizontal size={18} className="text-slate-500" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={() => setPreviewDevis(devis)} data-testid={`devis-view-${devis.id}`}>
                                                                        <Eye size={14} className="mr-2" /> Voir
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() => setEditingDevis(devis)}
                                                                        disabled={devisLocked(devis)}
                                                                        data-testid={`devis-edit-${devis.id}`}
                                                                    >
                                                                        <Pencil size={14} className="mr-2" /> Modifier{devisLocked(devis) ? ' (verrouillé)' : ''}
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem onClick={() => handleDownloadPdf(devis.id)} data-testid={`devis-download-${devis.id}`}>
                                                                        <Download size={14} className="mr-2" /> Télécharger
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                        {devis.version > 1 && (
                                                            <span className="text-xs text-slate-400 mt-1 block">v{devis.version}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Devis modifiés (historique) */}
                                            {historicFormuleDevis.map(devis => {
                                                const childDevis = formuleDevis.find(d => d.parent_devis_id === devis.id);
                                                const modifDate = devis.updated_at ? new Date(devis.updated_at) : null;
                                                return (
                                                    <div
                                                        key={devis.id}
                                                        className="p-4 rounded-lg border border-slate-100 bg-slate-50/70"
                                                        data-testid={`devis-hist-card-${devis.id}`}
                                                    >
                                                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center">
                                                            <div>
                                                                <span className="text-xs text-slate-400">N° Devis</span>
                                                                <p className="font-mono text-sm font-medium text-slate-500">{devis.numero_devis}</p>
                                                                <p className="text-[11px] text-red-500 mt-0.5 leading-tight">
                                                                    Modifié le {modifDate ? format(modifDate, "dd/MM/yyyy 'à' HH:mm", { locale: fr }) : '-'}
                                                                    {childDevis ? ` pour créer le devis ${childDevis.numero_devis}` : ''}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-slate-400">Créé le</span>
                                                                <p className="text-sm text-slate-500">{formatDate(devis.created_at)}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-slate-400">Volume</span>
                                                                <p className="text-sm text-slate-500">{devisVolume(devis)} m³</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-slate-400">Distance</span>
                                                                <p className="text-sm text-slate-500">{devisDistance(devis)} km</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-slate-400">Prix</span>
                                                                <p className="text-sm text-slate-500">
                                                                    {devis.prix_final != null ? `${devis.prix_final.toFixed(2)} €` : '-'}
                                                                </p>
                                                            </div>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="icon" data-testid={`devis-hist-actions-${devis.id}`}>
                                                                        <MoreHorizontal size={18} className="text-slate-400" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={() => setPreviewDevis(devis)} data-testid={`devis-hist-view-${devis.id}`}>
                                                                        <Eye size={14} className="mr-2" /> Voir
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem onClick={() => handleDownloadPdf(devis.id)} data-testid={`devis-hist-download-${devis.id}`}>
                                                                        <Download size={14} className="mr-2" /> Télécharger
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Bouton créer un autre devis */}
                                            <button
                                                type="button"
                                                onClick={() => handleCreateDevis(formule)}
                                                disabled={dossierLockedByFlag}
                                                className={`w-full flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors rounded-lg border border-dashed ${dossierLockedByFlag ? 'text-slate-300 border-slate-200 cursor-not-allowed' : 'text-slate-500 hover:text-[#2e7cd6] hover:bg-slate-50 border-slate-300'}`}
                                                data-testid={`create-devis-${formule}`}
                                                title={dossierLockedByFlag ? "Verrou actif : un devis a déjà été envoyé" : ''}
                                            >
                                                <Plus size={14} />
                                                Créer un devis {label}
                                            </button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}

                    {formules.length === 0 && (
                        <Card className="border-slate-200">
                            <CardContent className="py-12 text-center text-slate-400">
                                Aucune formule sélectionnée dans l'inventaire. Veuillez d'abord sélectionner des formules.
                            </CardContent>
                        </Card>
                    )}

                    {/* Section Historique emails envoyés */}
                    <Card className="border-slate-200" data-testid="email-history-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Mail size={16} className="text-[#2e7cd6]" />
                                Historique des emails envoyés
                                {emailHistory.length > 0 && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-2">
                                        {emailHistory.length}
                                    </Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {emailHistory.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-6">Aucun email envoyé</p>
                            ) : (
                                <div className="space-y-3">
                                    {emailHistory.map((email) => (
                                        <div
                                            key={email.id}
                                            className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50/50 transition-colors"
                                            data-testid={`email-history-${email.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-4 mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-[#2e7cd6]/10 flex items-center justify-center shrink-0">
                                                        <Send size={14} className="text-[#2e7cd6]" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-800">{email.sujet}</p>
                                                        <p className="text-xs text-slate-500">
                                                            Envoyé à <span className="font-medium text-slate-600">{email.destinataire}</span> par <span className="font-medium text-slate-600">{email.envoye_par}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-slate-400 shrink-0 whitespace-nowrap">
                                                    {formatDate(email.created_at)}
                                                </span>
                                            </div>
                                            {/* Devis joints */}
                                            <div className="flex flex-wrap gap-1.5 ml-10 mb-2">
                                                {(email.devis_envoyes || []).map((d, i) => (
                                                    <Badge key={i} variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                                                        {d.numero_devis} — {FORMULE_LABELS[d.formule] || d.formule}
                                                    </Badge>
                                                ))}
                                                {(!email.devis_envoyes || email.devis_envoyes.length === 0) && (
                                                    <span className="text-xs text-slate-400">{email.nb_devis} devis PDF</span>
                                                )}
                                            </div>
                                            {/* Message preview */}
                                            <div className="ml-10 bg-slate-50 rounded-md px-3 py-2 border border-slate-100">
                                                <p className="text-xs text-slate-500 leading-relaxed line-clamp-3 whitespace-pre-wrap">{email.message}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Dialog création devis */}
            <DevisFormDialog
                open={!!creatingFormule}
                onOpenChange={(open) => { if (!open) setCreatingFormule(null); }}
                inventaire={inventaire}
                formule={creatingFormule}
                formuleLabel={FORMULE_LABELS[creatingFormule] || creatingFormule}
                config={config}
                onSuccess={() => {
                    setCreatingFormule(null);
                    fetchData();
                }}
            />

            {/* Dialog aperçu devis */}
            <DevisFormDialog
                open={!!previewDevis}
                onOpenChange={(open) => { if (!open) setPreviewDevis(null); }}
                inventaire={inventaire}
                formule={previewDevis?.formule}
                formuleLabel={FORMULE_LABELS[previewDevis?.formule] || previewDevis?.formule}
                config={config}
                onSuccess={() => {}}
                mode="apercu"
                devisData={previewDevis}
            />

            {/* Dialog modification devis */}
            <DevisFormDialog
                open={!!editingDevis}
                onOpenChange={(open) => { if (!open) setEditingDevis(null); }}
                inventaire={inventaire}
                formule={editingDevis?.formule}
                formuleLabel={FORMULE_LABELS[editingDevis?.formule] || editingDevis?.formule}
                config={config}
                onSuccess={() => {
                    setEditingDevis(null);
                    fetchData();
                }}
                mode="modifier"
                devisData={editingDevis}
            />

            {/* Dialog aperçu PDF */}
            <Dialog open={!!pdfPreviewUrl} onOpenChange={(open) => { if (!open) handleClosePdfPreview(); }}>
                <DialogContent className="max-w-5xl w-full h-[90dvh] flex flex-col p-0 overflow-hidden" data-testid="pdf-preview-dialog">
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
                        <span className="text-sm font-medium text-slate-700 truncate">{pdfFilename}</span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={handleClosePdfPreview} data-testid="pdf-preview-close">Fermer</Button>
                            <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleConfirmDownload} data-testid="pdf-preview-download">
                                <Download size={14} className="mr-1.5" /> Télécharger
                            </Button>
                        </div>
                    </div>
                    <div className="flex-1 bg-slate-100">
                        {pdfPreviewUrl && (
                            <iframe src={pdfPreviewUrl} className="w-full h-full border-0" title="Aperçu PDF" />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog envoi email */}
            <SendDevisEmailDialog
                open={showEmailDialog}
                onClose={(sent) => {
                    setShowEmailDialog(false);
                    if (sent) fetchData();
                }}
                devisList={devisList}
                clientNom={clientNom}
                clientEmail={clientEmail}
                inventaireId={inventaireId}
                inventaire={inventaire}
            />


            {/* Dialog accepter devis */}
            <Dialog open={accepteDialog} onOpenChange={(open) => { if (!open) { setAccepteDialog(false); setAccepteDevisId(null); } }}>
                <DialogContent className="max-w-md" data-testid="accepte-devis-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 size={18} className="text-emerald-500" />
                            Accepter un devis
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-slate-600">
                            Choisissez le devis accepté par le client. Les autres devis actifs seront automatiquement refusés.
                        </p>
                        <div className="space-y-2">
                            {activeDevis.filter(d => d.statut !== 'accepte').map(d => (
                                <label
                                    key={d.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${accepteDevisId === d.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                                    data-testid={`accepte-choice-${d.id}`}
                                >
                                    <input
                                        type="radio"
                                        name="accepte-devis"
                                        checked={accepteDevisId === d.id}
                                        onChange={() => setAccepteDevisId(d.id)}
                                        className="accent-emerald-500"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-sm font-medium text-slate-800">{d.numero_devis}</span>
                                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                                {FORMULE_LABELS[d.formule] || d.formule}
                                            </Badge>
                                        </div>
                                        {d.prix_final != null && (
                                            <p className="text-sm text-slate-600 mt-0.5">{d.prix_final.toFixed(2)} EUR</p>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4">
                            <label className="text-sm font-medium text-emerald-800 mb-2 block">
                                Remarque <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={remarqueAccepte}
                                onChange={e => setRemarqueAccepte(e.target.value)}
                                placeholder="Ex: Accepté avec remise de 10%..."
                                className="text-sm"
                                data-testid="accepte-remarque"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setAccepteDialog(false); setAccepteDevisId(null); setRemarqueAccepte(''); }}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleAccepteConfirm}
                                disabled={!accepteDevisId || !remarqueAccepte.trim()}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid="accepte-confirm-btn"
                            >
                                <CheckCircle2 size={16} className="mr-2" />
                                Accepter ce devis
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog justificatif relance */}
            <Dialog open={!!relanceDialog} onOpenChange={(open) => { if (!open) { setRelanceDialog(null); setRelanceFile(null); } }}>
                <DialogContent className="max-w-md" data-testid="relance-justificatif-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Upload size={18} className="text-[#2e7cd6]" />
                            Justificatif — {DEVIS_STATUTS[relanceDialog]?.label}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-slate-600">
                            Pour passer en <strong className="text-[#2e7cd6]">{DEVIS_STATUTS[relanceDialog]?.label}</strong>, veuillez ajouter une preuve de relance (capture d'écran, photo...).
                        </p>

                        {/* Upload zone */}
                        <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-[#2e7cd6] hover:bg-blue-50/30 transition-colors" data-testid="relance-upload-zone">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => setRelanceFile(e.target.files?.[0] || null)}
                                data-testid="relance-file-input"
                            />
                            {relanceFile ? (
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 size={20} className="text-emerald-500" />
                                    <div>
                                        <p className="text-sm font-medium text-slate-700">{relanceFile.name}</p>
                                        <p className="text-xs text-slate-400">{(relanceFile.size / 1024).toFixed(0)} Ko</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Image size={32} className="text-slate-300 mb-2" />
                                    <p className="text-sm text-slate-500">Cliquez pour ajouter un justificatif</p>
                                    <p className="text-xs text-slate-400 mt-1">Image (max 5 Mo)</p>
                                </>
                            )}
                        </label>

                        {/* Already uploaded justificatifs */}
                        {relanceJustificatifs.filter(j => j.relance === relanceDialog).length > 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                <p className="text-xs text-emerald-700 font-medium">Justificatif existant :</p>
                                {relanceJustificatifs.filter(j => j.relance === relanceDialog).map((j, i) => (
                                    <p key={i} className="text-xs text-emerald-600 mt-1">{j.filename} — par {j.uploaded_by}</p>
                                ))}
                            </div>
                        )}

                        {/* Programmer relance 2 (only when changing to relance_1) */}
                        {relanceDialog === 'relance_1' && (
                            <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4">
                                <label className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                                    <CalendarDays size={16} className="text-amber-600" />
                                    Programmer la relance 2 <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-amber-600 mb-2">Si le devis reste en "Relancé 1" à cette date, il sera signalé à relancer une 2ème fois.</p>
                                <Input
                                    type="date"
                                    value={dateRelance2}
                                    onChange={e => setDateRelance2(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-[200px] text-sm mb-2"
                                    data-testid="relance-date-relance-2"
                                />
                                <div>
                                    <label className="text-xs text-amber-700 mb-1 block">Remarque (optionnel)</label>
                                    <Input
                                        value={remarqueRelance2}
                                        onChange={e => setRemarqueRelance2(e.target.value)}
                                        placeholder="Ex: Insister sur la promotion..."
                                        className="text-sm"
                                        data-testid="relance-remarque-relance-2"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Programmer relance 3 (only when changing to relance_2) */}
                        {relanceDialog === 'relance_2' && (
                            <div className="border border-rose-200 bg-rose-50/50 rounded-lg p-4">
                                <label className="text-sm font-medium text-rose-800 mb-2 flex items-center gap-2">
                                    <CalendarDays size={16} className="text-rose-600" />
                                    Programmer la relance 3 <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-rose-600 mb-2">Si le devis reste en "Relancé 2" à cette date, il sera signalé à relancer une 3ème fois.</p>
                                <Input
                                    type="date"
                                    value={dateRelance3}
                                    onChange={e => setDateRelance3(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-[200px] text-sm mb-2"
                                    data-testid="relance-date-relance-3"
                                />
                                <div>
                                    <label className="text-xs text-rose-700 mb-1 block">Remarque (optionnel)</label>
                                    <Input
                                        value={remarqueRelance3}
                                        onChange={e => setRemarqueRelance3(e.target.value)}
                                        placeholder="Ex: Dernière tentative..."
                                        className="text-sm"
                                        data-testid="relance-remarque-relance-3"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setRelanceDialog(null); setRelanceFile(null); setDateRelance2(''); setRemarqueRelance2(''); setDateRelance3(''); setRemarqueRelance3(''); }}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleRelanceConfirm}
                                disabled={!relanceFile || relanceUploading || (relanceDialog === 'relance_1' && !dateRelance2) || (relanceDialog === 'relance_2' && !dateRelance3)}
                                className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                data-testid="relance-confirm-btn"
                            >
                                {relanceUploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                                {relanceUploading ? 'Envoi...' : 'Valider la relance'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Intéressé */}
            <Dialog open={interesseDialog} onOpenChange={(open) => { if (!open) { setInteresseDialog(false); setRemarqueInteresse(''); } }}>
                <DialogContent className="max-w-md" data-testid="interesse-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ClipboardList size={18} className="text-cyan-600" />
                            Client intéressé — Remarque
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-slate-600">
                            Avant de passer en <strong className="text-cyan-600">Intéressé</strong>, veuillez ajouter une remarque décrivant l'intérêt du client.
                        </p>
                        <div className="border border-cyan-200 bg-cyan-50/50 rounded-lg p-4">
                            <label className="text-sm font-medium text-cyan-800 mb-2 block">
                                Remarque <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={remarqueInteresse}
                                onChange={e => setRemarqueInteresse(e.target.value)}
                                placeholder="Ex: Le client veut négocier le prix..."
                                className="text-sm"
                                data-testid="interesse-remarque"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setInteresseDialog(false); setRemarqueInteresse(''); }}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleInteresseConfirm}
                                disabled={!remarqueInteresse.trim() || interesseSaving}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                                data-testid="interesse-confirm-btn"
                            >
                                {interesseSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                                Valider
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Refusé */}
            <Dialog open={refuseDialog} onOpenChange={(open) => { if (!open) { setRefuseDialog(false); setRemarqueRefuse(''); } }}>
                <DialogContent className="max-w-md" data-testid="refuse-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ClipboardList size={18} className="text-red-600" />
                            Refuser le devis — Remarque
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-slate-600">
                            Avant de passer en <strong className="text-red-600">Refusé</strong>, veuillez ajouter une remarque.
                        </p>
                        <div className="border border-red-200 bg-red-50/50 rounded-lg p-4">
                            <label className="text-sm font-medium text-red-800 mb-2 block">
                                Remarque <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={remarqueRefuse}
                                onChange={e => setRemarqueRefuse(e.target.value)}
                                placeholder="Ex: Prix trop élevé pour le client..."
                                className="text-sm"
                                data-testid="refuse-remarque"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setRefuseDialog(false); setRemarqueRefuse(''); }}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleRefuseConfirm}
                                disabled={!remarqueRefuse.trim() || refuseSaving}
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid="refuse-confirm-btn"
                            >
                                {refuseSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                                Valider
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            </ReadOnlyGuard>
        </div>
    );
}
